import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DateInput from '../../components/forms/dateInput';
import { dateLocale } from '../../i18n/i18n';
import { tagLabel } from '../../constants/labels';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConfirmationModal from '../../components/modals/confirmation';
import SetTempEditOrderModal from '../../components/modals/orderEditTemp';
import { SkeletonRows } from '../../components/skeleton';
import Avatar from '../../components/avatar';
import { deleteRoutePlan, getRoutePlans, toRouteDate, updateRoutePlan, updateRouteStop } from '../../services/routePlans';
import { getUsersAsOptions } from '../../services/companyUsers';
import { getCurrentUser } from '../../services/auth';
import { onMutateSuccess } from '../../utils/queryResponse';
import { getCivicAddress, displayAddress1, intlPhoneDigits, toLocalDateString } from '../../utils/helper';
import { directionsLegs, mapsSearchUrl, stopLocation } from '../../utils/maps';
import { activityLabel, activityTime, canBeRouteAssignee } from '../../utils/routeActivity';
import { getStatusMeta } from '../../constants/jobStatus';
import { MISC_LABELS, ROUTE_STOP_STATUS_LABELS, assignmentLabel, roleListLabel, routeStopStatusLabel } from '../../constants/labels';
import { getBlankReceiptPdf } from '../../services/documents';
import { toast } from '../../utils/toast';

/* ── icons ── */
const IcChevL = () => <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>;
const IcChevR = () => <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>;
const IcUp = () => <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>;
const IcDown = () => <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>;
const IcX = () => <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
const IcPin = () => <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>;
const IcPhone = () => <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" /></svg>;
const IcWhats = () => <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.892c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652a11.94 11.94 0 0 0 5.71 1.454h.006c6.585 0 11.946-5.359 11.949-11.945a11.86 11.86 0 0 0-3.481-8.408" /></svg>;
const IcNav = () => <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>;
const IcPlus = () => <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
const IcCheck = () => <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>;
const IcUserX = () => <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="17" y1="8" x2="22" y2="13" /><line x1="22" y1="8" x2="17" y2="13" /></svg>;

const pageStyles = `
  .sbr .rp-date{display:flex;align-items:center;gap:6px}
  .sbr .rp-date button{width:34px;height:34px;border:1px solid var(--line);border-radius:9px;background:#fff;color:var(--ink);display:inline-flex;align-items:center;justify-content:center;cursor:pointer}
  .sbr .rp-date input{border:1px solid var(--line);border-radius:9px;padding:7px 10px;font:700 13px inherit;color:var(--ink);background:#fff;outline:0}
  .sbr .rp-date .date-input input{width:116px;text-align:center;cursor:pointer}
  .sbr .rp-date .today{padding:0 10px;width:auto;font:700 12px inherit}
  .sbr .rp-tabs{display:flex;gap:6px;flex-wrap:wrap;margin:14px 0}
  .sbr .rp-tabs button{border:1px solid var(--line);border-radius:999px;padding:7px 12px;background:#fff;font:700 12.5px inherit;color:var(--text);cursor:pointer}
  .sbr .rp-tabs button.on{border-color:var(--brand);background:var(--brand-50);color:var(--brand-ink)}
  .sbr .rp-plan{background:#fff;border:1px solid var(--line);border-radius:var(--r);box-shadow:var(--sh-1);padding:16px;margin-bottom:14px}
  .sbr .rp-plan-h{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .sbr .rp-plan-h .nm{border:0;border-bottom:1px dashed transparent;font:800 17px inherit;color:var(--ink);background:transparent;outline:0;min-width:120px;max-width:100%}
  .sbr .rp-plan-h .nm:focus,.sbr .rp-plan-h .nm:hover{border-bottom-color:var(--line)}
  .sbr .rp-plan-h .drv{display:flex;align-items:center;gap:8px;font-size:12.5px;font-weight:700;color:var(--text)}
  .sbr .rp-plan-h .drv select{border:1px solid var(--line);border-radius:8px;padding:6px 8px;font:700 12.5px inherit;color:var(--ink);background:#fff}
  .sbr .rp-plan-h .sp{margin-left:auto;display:flex;gap:8px;flex-wrap:wrap;align-items:center}
  .sbr .rp-pstat{font-size:10.5px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;padding:3px 8px;border-radius:999px;background:var(--wf-grey-bg);color:var(--wf-grey)}
  .sbr .rp-pstat.IN_PROGRESS{background:var(--wf-amber-bg);color:var(--wf-amber)}
  .sbr .rp-pstat.COMPLETED{background:var(--wf-green-bg,#E6F6EE);color:var(--wf-green,#187A46)}
  .sbr .rp-notice{display:flex;gap:10px;align-items:flex-start;padding:10px 14px;margin-bottom:14px;border-radius:12px;background:var(--warn-bg);border:1px solid var(--warn-bd);font-size:12.5px;font-weight:600;color:var(--ink)}
  .sbr .rp-notice ul{margin:4px 0 0;padding-left:18px}
  .sbr .rp-notice .x{margin-left:auto;background:none;border:0;color:var(--muted);cursor:pointer}
  .sbr .rp-legs{display:flex;gap:8px;flex-wrap:wrap;margin-top:12px}
  .sbr .rp-legs a{display:inline-flex;align-items:center;gap:6px}
  .sbr .rp-stops{display:flex;flex-direction:column;gap:10px;margin-top:14px}
  .sbr .rp-stop{display:grid;grid-template-columns:34px minmax(0,1fr) auto;gap:6px 12px;align-items:start;padding:12px 14px;border:1px solid var(--line);border-radius:12px;background:#fff}
  .sbr .rp-stop.DONE{background:#F6FBF8;border-color:#CDEBD9}
  .sbr .rp-stop.CUSTOMER_UNAVAILABLE{background:#FFF7F7;border-color:#F3CBCB}
  .sbr .rp-stop.SKIPPED{opacity:.7}
  .sbr .rp-stop .num{width:34px;height:34px;border-radius:50%;background:var(--brand);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:13px}
  .sbr .rp-stop.DONE .num{background:#187A46}
  .sbr .rp-stop.CUSTOMER_UNAVAILABLE .num{background:var(--bad)}
  .sbr .rp-stop .top{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
  .sbr .rp-stop .cust{font-weight:800;font-size:14px;color:var(--ink)}
  .sbr .rp-stop .jno{font-size:12px;font-weight:800;color:var(--muted)}
  .sbr .rp-stop .act{font-size:10.5px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;padding:3px 8px;border-radius:999px;background:var(--brand-50);color:var(--brand-ink)}
  .sbr .rp-stop .act.DELIVERY{background:#E6F6EE;color:#187A46}
  .sbr .rp-stop .act.ON_SITE{background:#FFF4D6;color:#8A6100}
  .sbr .rp-stop .st{font-size:10.5px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;padding:3px 8px;border-radius:999px;background:var(--wf-grey-bg);color:var(--wf-grey)}
  .sbr .rp-stop .st.DONE{background:#E6F6EE;color:#187A46}
  .sbr .rp-stop .st.CUSTOMER_UNAVAILABLE{background:var(--bad-bg);color:var(--bad)}
  .sbr .rp-stop .addr{font-size:13px;color:var(--text);font-weight:600;margin-top:4px;overflow-wrap:anywhere}
  .sbr .rp-stop .meta{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:6px;font-size:12.5px;font-weight:700;color:var(--muted)}
  .sbr .rp-stop .meta a{display:inline-flex;align-items:center;gap:5px;color:var(--brand);text-decoration:none}
  .sbr .rp-stop .meta .when{font-variant-numeric:tabular-nums;color:var(--ink)}
  .sbr .rp-stop .ctl{display:flex;flex-direction:column;gap:4px}
  .sbr .rp-stop .ctl button{width:32px;height:30px;border:1px solid var(--line);border-radius:8px;background:#fff;color:var(--text);display:inline-flex;align-items:center;justify-content:center;cursor:pointer}
  .sbr .rp-stop .ctl button:disabled{opacity:.35;cursor:not-allowed}
  .sbr .rp-stop .ctl button.rm{color:var(--bad)}
  .sbr .rp-stop .acts{grid-column:2 / -1;display:flex;gap:8px;flex-wrap:wrap;margin-top:8px}
  .sbr .rp-stop .acts .btn-sbr{display:inline-flex;align-items:center;gap:6px}
  .sbr .rp-stop .acts .btn-bad{border-color:var(--bad);color:var(--bad);background:#fff}
  .sbr .rp-stop .acts .btn-ok{background:#187A46;border-color:#187A46;color:#fff}
  .sbr .rp-stop .note{grid-column:2 / -1;display:flex;flex-direction:column;gap:10px;margin-top:10px;padding:12px;border:1px solid var(--line);border-radius:12px;background:var(--surface,#F8FAFC)}
  .sbr .rp-stop .note textarea{display:block;width:100%;box-sizing:border-box;border:1px solid var(--line);border-radius:10px;padding:10px 12px;font:500 13.5px/1.45 inherit;color:var(--ink);background:#fff;min-height:78px;resize:vertical;outline:0;transition:border-color .14s,box-shadow .14s}
  .sbr .rp-stop .note textarea:focus{border-color:var(--brand);box-shadow:0 0 0 3px var(--brand-50)}
  .sbr .rp-stop .note .row{display:flex;gap:8px;flex-wrap:wrap}
  .sbr .rp-stop .note .btn-sbr{display:inline-flex;align-items:center;justify-content:center;gap:6px}
  .sbr .rp-stop .note .btn-bad{border:1px solid var(--bad);color:var(--bad);background:#fff}
  .sbr .rp-stop .note .btn-bad:hover:not(:disabled){background:var(--bad-bg)}
  .sbr .rp-stop .stopnote{font-size:12.5px;color:var(--bad);font-weight:600;margin-top:4px}
  .sbr .rp-empty{text-align:center;padding:40px 20px;color:var(--muted)}
  .sbr .rp-refreshing{display:flex;align-items:center;gap:8px;font-size:12.5px;font-weight:700;color:var(--muted);margin-bottom:10px}
  .sbr .rp-refreshing .sp{width:13px;height:13px;border:2px solid var(--line);border-top-color:var(--brand);border-radius:50%;animation:rpSpin .7s linear infinite}
  .sbr .btn-sbr .sp{width:13px;height:13px;border:2px solid rgba(255,255,255,.45);border-top-color:#fff;border-radius:50%;animation:rpSpin .7s linear infinite}
  .sbr .btn-ghost-sbr .sp,.sbr .btn-bad .sp{border-color:var(--line);border-top-color:var(--brand)}
  @keyframes rpSpin{to{transform:rotate(360deg)}}
  @media(max-width:899px){
    .sbr .rp-stop{grid-template-columns:30px minmax(0,1fr) auto;padding:12px}
    .sbr .rp-stop .num{width:30px;height:30px;font-size:12px}
    .sbr .rp-stop .ctl{flex-direction:row}
    .sbr .rp-stop .acts .btn-sbr,.sbr .rp-stop .note .row .btn-sbr{flex:1;justify-content:center}
  }
`;

// Plan status labels are now translated via t() inside the component.
const CLOSED_STOP = ['DONE', 'SKIPPED', 'CUSTOMER_UNAVAILABLE'];

const shiftDate = (ymd, days) => {
    const d = new Date(`${ymd}T12:00:00`);
    d.setDate(d.getDate() + days);
    return toRouteDate(d);
};

// The same rule as the API: the FIRST active role decides. A driver or
// technician gets "My Route"; the office gets the planner.
const isFieldRole = () => {
    const role = (getCurrentUser()?.activeRoles || [])[0];
    return role === 'driver' || role === 'cleaner';
};

const RoutePlanPage = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const fieldView = isFieldRole();
    // Paper path for customers who cannot use a digital receipt: open the
    // prefilled A4 receipt for this stop so it can be printed before the route.
    const printBlankReceipt = async (jobId) => {
        try {
            const blob = await getBlankReceiptPdf(jobId, 'A4')
            const url = URL.createObjectURL(blob)
            const win = window.open(url, '_blank', 'noopener')
            if (!win) toast.info(t('routePlan.receiptPopup'), { position: toast.POSITION.BOTTOM_RIGHT })
        } catch (e) {
            toast.error(e?.message || t('routePlan.receiptError'), { position: toast.POSITION.BOTTOM_RIGHT })
        }
    }
    const title = fieldView ? MISC_LABELS.myRoute : MISC_LABELS.routePlan;

    const date = searchParams.get('date') || toRouteDate();
    const planParam = searchParams.get('plan');

    const setDate = (next) => setSearchParams({ date: next });
    const selectPlan = (id) => setSearchParams({ date, plan: id });

    // Routes change from outside this page — the office builds one in the
    // add-to-route drawer and lands here, and a driver's stops are marked from
    // their own device. The global 24-hour staleTime would serve whatever was
    // cached before that happened (a route created moments ago showed an empty
    // page until a manual refresh), so always refetch on arrival.
    const { data, isLoading, isFetching } = useQuery(['route-plans', date], () => getRoutePlans(date), {
        staleTime: 30 * 1000,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });
    const plans = useMemo(() => (data?.code === 200 && Array.isArray(data.message) ? data.message : []), [data]);
    const plan = plans.find(p => p.id === planParam) || plans[0] || null;

    const { data: usersData } = useQuery(['users-option'], getUsersAsOptions, { enabled: !fieldView });
    const assignees = useMemo(() => {
        const all = usersData?.code === 200 && Array.isArray(usersData.message) ? usersData.message : [];
        return all.filter(canBeRouteAssignee);
    }, [usersData]);

    // Local stop order so reordering feels instant; saved on every move.
    const [stops, setStops] = useState([]);
    useEffect(() => { setStops(plan?.stops || []); }, [plan?.id, plan?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const [name, setName] = useState('');
    useEffect(() => { setName(plan?.name || ''); }, [plan?.id, plan?.name]);

    const [confirmDelete, setConfirmDelete] = useState(false);
    const [editJob, setEditJob] = useState(null);
    // Job slots that were held by someone else before the route assignment
    // overwrote them — shown once so the office knows who lost the job.
    const [reassigned, setReassigned] = useState([]);
    // Stop whose "customer unavailable" note is being typed.
    const [noteFor, setNoteFor] = useState(null);
    const [noteText, setNoteText] = useState('');

    const refresh = () => queryClient.invalidateQueries(['route-plans']);

    const { mutate: save, isLoading: saving } = useMutation(updateRoutePlan, {
        onSuccess: onMutateSuccess((result) => {
            refresh();
            if (Array.isArray(result?.reassigned) && result.reassigned.length) setReassigned(result.reassigned);
        }),
    });
    const { mutate: remove } = useMutation(deleteRoutePlan, {
        onSuccess: onMutateSuccess(() => { refresh(); setSearchParams({ date }); }, t('routePlan.routePlanDeleted')),
    });
    // `variables` tells us WHICH stop is in flight, so only that stop's button
    // shows a spinner instead of every stop on the route going busy at once.
    const { mutate: markStop, isLoading: marking, variables: markVars } = useMutation(updateRouteStop, {
        onSuccess: onMutateSuccess(() => {
            refresh();
            queryClient.invalidateQueries(['jobs']);
            queryClient.invalidateQueries(['notifications']);
            setNoteFor(null); setNoteText('');
        }),
    });

    const saveStops = (next) => {
        setStops(next);
        save({ id: plan.id, stops: next.map(s => ({ jobId: s.jobId, activity: s.activity })) });
    };
    const move = (index, delta) => {
        const to = index + delta;
        if (to < 0 || to >= stops.length) return;
        const next = [...stops];
        [next[index], next[to]] = [next[to], next[index]];
        saveStops(next);
    };
    const removeStop = (index) => saveStops(stops.filter((_, i) => i !== index));

    const setStopStatus = (s, status, note) => markStop({ id: plan.id, stopId: s.id, status, ...(note !== undefined ? { note } : {}) });

    const legs = directionsLegs(stops.map(s => stopLocation(s.job?.client)));
    const doneCount = stops.filter(s => CLOSED_STOP.includes(s.status)).length;

    const dateLabel = new Date(`${date}T12:00:00`).toLocaleDateString(dateLocale(), { weekday: 'long', day: 'numeric', month: 'long' });

    // Rendered by CALLING these, never as JSX elements. Declared
    // inside the component they close over state, so as JSX elements React sees
    // a new component type on every render and remounts the subtree — which
    // destroyed and recreated the note textarea on each keystroke, re-firing
    // autoFocus and putting the caret back at position 0. Typing "asldjls" came
    // out as "sljdlsa", which reads like an RTL bug but is a remount.
    const dateNav = () => (
        <div className="rp-date">
            <button type="button" onClick={() => setDate(shiftDate(date, -1))} aria-label={t('routePlan.previousDay')}><IcChevL /></button>
            <DateInput inline value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
            <button type="button" onClick={() => setDate(shiftDate(date, 1))} aria-label={t('routePlan.nextDay')}><IcChevR /></button>
            {date !== toRouteDate() && <button type="button" className="today" onClick={() => setDate(toRouteDate())}>{t('routePlan.today')}</button>}
        </div>
    );

    const addJobsHref = `/jobs?select=1&routeDate=${date}${plan ? `&plan=${plan.id}` : ''}`;

    const stopActions = (s) => {
        if (!s.id) return null;
        const closed = CLOSED_STOP.includes(s.status);
        const c = s.job?.client;
        // Busy only for the stop actually being saved, not the whole route.
        const busyStop = marking && markVars?.stopId === s.id;
        if (noteFor === s.id) {
            return (
                <div className="note">
                    <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder={t('routePlan.notePrompt')} autoFocus />
                    <div className="row">
                        <button type="button" className="btn-sbr btn-sm-sbr btn-bad" disabled={marking} onClick={() => setStopStatus(s, 'CUSTOMER_UNAVAILABLE', noteText || null)}>
                            {busyStop ? <><span className="sp" aria-hidden="true" /> {t('routePlan.savingStop')}</> : <><IcUserX /> {t('routePlan.confirmUnavailable', { status: ROUTE_STOP_STATUS_LABELS.CUSTOMER_UNAVAILABLE.toLowerCase() })}</>}
                        </button>
                        <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" disabled={busyStop} onClick={() => { setNoteFor(null); setNoteText(''); }}>{t('common.cancel')}</button>
                    </div>
                </div>
            );
        }
        return (
            <div className="acts">
                {!closed && <button type="button" className="btn-sbr btn-sm-sbr btn-ok" disabled={marking} onClick={() => setStopStatus(s, 'DONE')}>
                    {busyStop ? <><span className="sp" aria-hidden="true" /> {t('routePlan.savingStop')}</> : <><IcCheck /> {ROUTE_STOP_STATUS_LABELS.DONE}</>}
                </button>}
                {!closed && <button type="button" className="btn-sbr btn-sm-sbr btn-bad" disabled={marking} onClick={() => { setNoteFor(s.id); setNoteText(s.note || ''); }}><IcUserX /> {ROUTE_STOP_STATUS_LABELS.CUSTOMER_UNAVAILABLE}</button>}
                {closed && <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" disabled={marking} onClick={() => setStopStatus(s, 'PENDING')}>
                    {busyStop ? <><span className="sp" aria-hidden="true" /> {t('routePlan.savingStop')}</> : t('routePlan.reopen')}
                </button>}
                {c && <a className="btn-sbr btn-ghost-sbr btn-sm-sbr" href={mapsSearchUrl(c)} target="_blank" rel="noreferrer"><IcPin /> {t('routePlan.openInMaps')}</a>}
            </div>
        );
    };

    return (
        <div className="sb-page">
            <style>{pageStyles}</style>
            <ConfirmationModal display={confirmDelete} toggle={setConfirmDelete}
                title={t('routePlan.deleteRoutePlan')} message={t('routePlan.deleteMessage')}
                confirmAction={() => plan && remove(plan.id)} />
            {editJob && <SetTempEditOrderModal display={!!editJob} toggle={() => setEditJob(null)} jobId={editJob} />}

            <div className="sbr">
                <div className="dhead sbr-d">
                    <div>
                        <div className="ttl">{title}</div>
                        <div className="sub">{dateLabel}{plans.length > 0 && ` · ${t('routePlan.routes', { count: plans.length })}`}</div>
                    </div>
                    <div className="act" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {dateNav()}
                        {!fieldView && <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => navigate(addJobsHref)}><IcPlus /> {t('routePlan.addJobs')}</button>}
                    </div>
                </div>
                <div className="sbr-m" style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.8px', color: 'var(--ink)' }}>{title}</div>
                    <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, marginTop: 2, marginBottom: 10 }}>{dateLabel}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {dateNav()}
                        {!fieldView && <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => navigate(addJobsHref)}><IcPlus /> {t('routePlan.addJobs')}</button>}
                    </div>
                </div>

                {reassigned.length > 0 && (
                    <div className="rp-notice" role="status">
                        <div>
                            <b>{t('routePlan.assignmentsChanged')}</b> {t('routePlan.nowGoTo', { name: plan?.driver?.fullName || t('routePlan.routeAssignee') })}
                            <ul>
                                {reassigned.map((r, i) => (
                                    <li key={`${r.jobId}-${r.slot}-${i}`}>{t('routePlan.reassignedItem', { id: r.jobId, slot: assignmentLabel(r.slot), name: r.previousAssignee?.fullName || t('routePlan.someoneElse') })}</li>
                                ))}
                            </ul>
                        </div>
                        <button type="button" className="x" onClick={() => setReassigned([])} aria-label={t('routePlan.dismiss')}><IcX /></button>
                    </div>
                )}

                {isLoading && <div className="card-sbr"><SkeletonRows rows={5} widths={['60%', '40%']} /></div>}

                {/* A refetch over cached data leaves isLoading false, so without
                    this the page sits silently on stale stops — the case a driver
                    hits every time they reopen the tab. */}
                {!isLoading && isFetching && (
                    <div className="rp-refreshing" role="status"><span className="sp" aria-hidden="true" />{t('routePlan.updatingRoute')}</div>
                )}

                {!isLoading && plans.length === 0 && (
                    <div className="card-sbr rp-empty">
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{fieldView ? t('routePlan.noRouteField') : t('routePlan.noRoutePlanned')}</div>
                        {!fieldView
                            ? <div style={{ fontSize: 12.5, marginTop: 6 }}>{t('routePlan.openJobsTip')} <b>{t('routePlan.addToRoute')}</b>.</div>
                            : <div style={{ fontSize: 12.5, marginTop: 6 }}>{t('routePlan.officeAssignTip')}</div>}
                        {!fieldView && <button className="btn-sbr btn-primary-sbr btn-sm-sbr" style={{ marginTop: 14 }} onClick={() => navigate(addJobsHref)}><IcPlus /> {t('routePlan.pickJobs')}</button>}
                    </div>
                )}

                {plans.length > 1 && (
                    <div className="rp-tabs">
                        {plans.map(p => (
                            <button key={p.id} type="button" className={plan?.id === p.id ? 'on' : ''} onClick={() => selectPlan(p.id)}>
                                {p.name || (p.driver ? p.driver.fullName : t('routePlan.route'))} · {p.stops.length}
                            </button>
                        ))}
                    </div>
                )}

                {plan && (
                    <div className="rp-plan">
                        <div className="rp-plan-h">
                            {fieldView
                                ? <div style={{ font: '800 17px inherit', color: 'var(--ink)' }}>{plan.name || t('routePlan.route')}</div>
                                : <input className="nm" value={name} placeholder={t('routePlan.routeName')} onChange={(e) => setName(e.target.value)}
                                    onBlur={() => name !== (plan.name || '') && save({ id: plan.id, name })} />}
                            <div className="drv">
                                {plan.driver && <Avatar size={26} name={plan.driver.fullName} src={plan.driver.avatarUrl} />}
                                {fieldView
                                    ? <span>{plan.driver?.fullName || t('routePlan.notAssigned')}</span>
                                    : <>
                                        <span>{MISC_LABELS.assignedTo}</span>
                                        <select value={plan.driverId || ''} onChange={(e) => save({ id: plan.id, driverId: e.target.value || null })} aria-label={MISC_LABELS.assignedTo}>
                                            <option value="">{t('routePlan.notAssigned')}</option>
                                            {assignees.map(u => <option key={u.id} value={u.id}>{u.fullName}{u.roles ? ` · ${roleListLabel(u.roles)}` : ''}</option>)}
                                        </select>
                                    </>}
                            </div>
                            <div className="sp">
                                {plan.status && <span className={`rp-pstat ${plan.status}`}>{plan.status === 'PLANNED' ? t('routePlan.planned') : plan.status === 'IN_PROGRESS' ? t('routePlan.inProgress') : plan.status === 'COMPLETED' ? t('routePlan.completed') : plan.status}</span>}
                                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>
                                    {doneCount > 0 ? `${doneCount}/${stops.length} ${t('routePlan.done')}` : t('routePlan.stop', { count: stops.length })}{saving ? ` · ${t('routePlan.savingEllipsis')}` : ''}
                                </span>
                                {!fieldView && <button className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => setConfirmDelete(true)}>{t('common.delete')}</button>}
                            </div>
                        </div>

                        {legs.length > 0 && (
                            <div className="rp-legs">
                                {legs.map((leg, i) => (
                                    <a key={i} className="btn-sbr btn-primary-sbr btn-sm-sbr" href={leg.url} target="_blank" rel="noreferrer">
                                        <IcNav /> {legs.length === 1 ? t('routePlan.openRouteGMaps') : t('routePlan.leg', { n: i + 1, first: leg.first, last: leg.last })}
                                    </a>
                                ))}
                                {legs.length > 1 && <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center', fontWeight: 600 }}>{t('routePlan.legNote')}</span>}
                            </div>
                        )}

                        <div className="rp-stops">
                            {stops.length === 0 && <div className="rp-empty">{fieldView ? t('routePlan.noStopsField') : t('routePlan.noStopsYet')}</div>}
                            {stops.map((s, i) => {
                                const job = s.job || {};
                                const c = job.client;
                                const when = activityTime(job, s.activity);
                                const sameDay = when && toRouteDate(when) === date;
                                const status = job.status ? getStatusMeta(job.status) : null;
                                const digits = intlPhoneDigits(c?.phone);
                                const badges = Array.isArray(job.badges) ? job.badges.map(b => tagLabel(b)).filter(Boolean) : [];
                                const stopStatus = s.status || 'PENDING';
                                return (
                                    <div key={s.id || `${s.jobId}-${s.activity}`} className={`rp-stop ${stopStatus}`}>
                                        <div className="num">{stopStatus === 'DONE' ? <IcCheck /> : i + 1}</div>
                                        <div style={{ minWidth: 0 }}>
                                            <div className="top">
                                                <span className="cust">{c ? `${c.firstName} ${c.lastName}` : '—'}</span>
                                                <span className={`act ${s.activity}`}>{activityLabel(s.activity)}</span>
                                                {stopStatus !== 'PENDING' && <span className={`st ${stopStatus}`}>{routeStopStatusLabel(stopStatus)}</span>}
                                                <button type="button" className="jno" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }} onClick={() => setEditJob(job.id)}>#{job.id}</button>
                                                {badges.map(n => <span key={n} className="jno">T {n}</span>)}
                                                {status && <span className={`badge-sbr b-${status.tone}`}>{status.label}</span>}
                                                {job.needsRescheduleSince && <span className="flag late" style={{ marginLeft: 0 }}>{MISC_LABELS.needsRescheduling}</span>}
                                            </div>
                                            <div className="addr">
                                                {getCivicAddress(c) || '—'}
                                                {c && displayAddress1(c) && <span style={{ color: 'var(--muted)' }}> · {displayAddress1(c)}</span>}
                                            </div>
                                            <div className="meta">
                                                <span className="when">{when ? (sameDay ? toLocalDateString(when, 'HH:mm') : toLocalDateString(when, 'dd.MM HH:mm')) : MISC_LABELS.notScheduled}</span>
                                                {c?.phone && <a href={`tel:${c.phone}`}><IcPhone /> {c.phone}</a>}
                                                {digits && <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer"><IcWhats /> WhatsApp</a>}
                                                {!fieldView && c && <a href={mapsSearchUrl(c)} target="_blank" rel="noreferrer"><IcPin /> {t('routePlan.openInMaps')}</a>}
                                                {s.activity === 'PICKUP' &&
                                                    <button type="button" onClick={() => printBlankReceipt(job.id)}
                                                        style={{ background: 'none', border: 0, padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
                                                        title={t('routePlan.printReceiptTitle')}>
                                                        {t('routePlan.blankReceipt')}
                                                    </button>
                                                }
                                                {s.statusAt && stopStatus !== 'PENDING' && <span>{routeStopStatusLabel(stopStatus)} {toLocalDateString(s.statusAt, 'HH:mm')}</span>}
                                            </div>
                                            {job.note && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>{job.note}</div>}
                                            {s.note && stopStatus !== 'PENDING' && <div className="stopnote">{s.note}</div>}
                                        </div>
                                        <div className="ctl">
                                            <button type="button" onClick={() => move(i, -1)} disabled={i === 0 || saving} aria-label={t('routePlan.moveUp')}><IcUp /></button>
                                            <button type="button" onClick={() => move(i, 1)} disabled={i === stops.length - 1 || saving} aria-label={t('routePlan.moveDown')}><IcDown /></button>
                                            {!fieldView && <button type="button" className="rm" onClick={() => removeStop(i)} disabled={saving} aria-label={t('routePlan.removeStop')}><IcX /></button>}
                                        </div>
                                        {stopActions(s)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RoutePlanPage;
